import { hasActivePaidSubscription, isGuestUser } from "@hey-lady/shared/helpers/membershipAccess";
import { isAdminOrTeam } from "@hey-lady/shared/hooks/event";
import { useAuth } from "@hey-lady/shared/hooks/user";
import {
  EventPermissions,
  SubscriptionStatus,
  SubscriptionTier,
  UserBadgeType,
  useGetPlanStatusQuery,
  useGuestPassSummaryQuery,
  useHasSeenEventsTabAfterAttendedThreeEventsQuery,
} from "@hey-lady/shared/types/graphql";
import { not } from "ramda";
import React, { createContext, type ReactNode, useContext } from "react";
import { EVENTS_TO_UNLOCK } from "../components/Filters/consts";
import { isPostReleaseMember } from "../components/Filters/helpers";

interface UnlockedEventsContextType {
  hasSeenEventsTabAfterAttendedThreeEvents: boolean | undefined;
  loading: boolean;
  attendedEventsCount: number;
  isPostReleaseUser: boolean;
  hasAdvancedFiltersUnlocked: boolean;
  isGuestPassUser: boolean;
  /** Her Guest Pass is running: she gets Guest Pass Home */
  hasActiveGuestPass: boolean;
  guestPassLoading: boolean;
  /** A trial without a Guest Pass (e.g. a Basic plan trial): she gets the same Home */
  isRegularTrial: boolean;
  /** When her trial ends */
  trialEndsAt?: string;
  planLoading: boolean;
  /** Paid and still in her first month: the Hey Lady! Way and a simpler Home */
  isLittleSister: boolean;
  hasFullEventAccessFromGuestConversion: boolean;
  bypassesAttendanceLock: boolean;
  subscriptionTier: SubscriptionTier;
}

const UnlockedEventsContext = createContext<UnlockedEventsContextType | undefined>(undefined);

interface Props {
  children: ReactNode;
}

export const UnlockedEventsProvider: React.FC<Props> = ({ children }) => {
  const { me } = useAuth();

  const attendedEventsCount = me?.eventStatistics?.totalAttendedScheduledEvents || 0;
  const subscriptionTier = me?.subscriptionTier;
  const isPostReleaseUser = isPostReleaseMember(me?.createdAt);
  const isStaff = Boolean(me) && isAdminOrTeam(me);
  const hasGuestBadge = isGuestUser(me);
  const { data: guestPassData, loading: guestPassLoading } = useGuestPassSummaryQuery({
    fetchPolicy: "cache-first",
  });
  const hasActiveGuestPass = Boolean(guestPassData?.guestPassSummary?.isActive);
  const isGuestPassUser =
    hasGuestBadge || Boolean(guestPassData?.guestPassSummary?.canActivateMembership);
  const hasFullEventAccessFromGuestConversion = Boolean(
    guestPassData?.guestPassSummary &&
      !guestPassData.guestPassSummary.canActivateMembership &&
      !hasGuestBadge,
  );
  const { data: planData, loading: planLoading } = useGetPlanStatusQuery({
    fetchPolicy: "cache-first",
  });
  const isRegularTrial =
    planData?.planDetails?.status === SubscriptionStatus.InTrial &&
    !hasActiveGuestPass &&
    !hasGuestBadge;
  const isLittleSister =
    me?.kind === EventPermissions.ZeroToOne &&
    hasActivePaidSubscription(planData?.planDetails?.status);
  // Only a trial keeps the three-events lock: once the membership is active it
  // is lifted, whether she came from a Guest Pass or paid directly.
  const bypassesAttendanceLock =
    hasFullEventAccessFromGuestConversion ||
    hasActivePaidSubscription(planData?.planDetails?.status);

  const skip =
    isStaff ||
    isGuestPassUser ||
    bypassesAttendanceLock ||
    not(isPostReleaseUser) ||
    me.badges?.some((b) => b.type === UserBadgeType.EventsParticipated_10) ||
    attendedEventsCount < EVENTS_TO_UNLOCK;

  const { data, loading } = useHasSeenEventsTabAfterAttendedThreeEventsQuery({
    fetchPolicy: "cache-and-network",
    skip,
  });

  const hasAdvancedFiltersUnlocked =
    !isGuestPassUser &&
    (isStaff ||
      bypassesAttendanceLock ||
      not(isPostReleaseUser) ||
      attendedEventsCount >= EVENTS_TO_UNLOCK);

  const value = {
    hasSeenEventsTabAfterAttendedThreeEvents: skip
      ? true
      : data?.hasSeenEventsTabAfterAttendedThreeEvents,
    loading,
    attendedEventsCount,
    isPostReleaseUser,
    hasAdvancedFiltersUnlocked,
    isGuestPassUser,
    hasActiveGuestPass,
    guestPassLoading,
    isRegularTrial,
    trialEndsAt: isRegularTrial ? planData?.planDetails?.end : undefined,
    planLoading,
    isLittleSister,
    hasFullEventAccessFromGuestConversion,
    bypassesAttendanceLock,
    subscriptionTier,
  };

  return <UnlockedEventsContext.Provider value={value}>{children}</UnlockedEventsContext.Provider>;
};

export const useUnlockedEvents = (): UnlockedEventsContextType => {
  const context = useContext(UnlockedEventsContext);
  if (context === undefined) {
    throw new Error("useUnlockedEvents must be used within an UnlockedEventsProvider");
  }
  return context;
};
